import { useEffect, useState } from 'react';

import { AppIcon, ICON_SIZES, VideoUpload, ConfirmDelete } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { StoryDocument, profileApi } from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Box, Typography, SxProps, Theme, Chip, Avatar } from '@mui/material';

import { FileUploadConfig } from '../types/profile.types';
import { handleDeleteStory } from '../utils/deleteStory.util';

const DELETE_STRINGS = appStrings.modules.profile.edit;

type ProfileVideoViewProps = Readonly<{
  fileUploadConfig?: FileUploadConfig;
  storyDocuments: StoryDocument[];
  user?: {
    first_name?: string;
    last_name?: string;
    profile_image?: string;
  };
  showDelete?: boolean;
  fixedWidth?: boolean;
}>;

export const ProfileVideoView = ({
  storyDocuments,
  user,
  showDelete = true,
  fixedWidth = true,
}: ProfileVideoViewProps) => {
  const dispatch = useAppDispatch();
  const [localStoryDocuments, setLocalStoryDocuments] = useState<StoryDocument[]>(storyDocuments);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<StoryDocument | null>(null);

  const handleDeleteClick = (document: StoryDocument) => {
    setSelectedDocument(document);
    setOpenDialog(true);
  };

  const handleDeleteSuccess = (deletedId: number) => {
    setLocalStoryDocuments((prev) => prev.filter((doc) => doc.id !== deletedId));

    // now invalidate the user profile cache to refetch
    dispatch(profileApi.util.invalidateTags(['UserProfile']));
  };

  useEffect(() => {
    setLocalStoryDocuments(storyDocuments);
  }, [storyDocuments]);

  const videoTypeStories = localStoryDocuments.filter((doc) => doc.media_type === 'video');

  return (
    <>
      <ConfirmDelete
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        onSuccess={handleDeleteSuccess}
        headerText={DELETE_STRINGS.deleteStoryConfirmHeader}
        bodyText={DELETE_STRINGS.deleteStoryConfirmBody}
        handleDelete={() =>
          handleDeleteStory({
            selectedDocument,
            onSuccess: handleDeleteSuccess,
            setOpenDialog,
            setError: () => null,
          })
        }
      />

      <Box sx={documentsContainerSx(fixedWidth)}>
        {videoTypeStories.map((document) => (
          <Box key={document.id} sx={videoCardContainerSx}>
            <VideoUpload mode="view" videoUrl={document.media_url} roundedCorners={false} />

            <Box sx={contentContainerSx}>
              <Box sx={userAndTrashContainerSx}>
                {user && (
                  <Box sx={chipContainerSx}>
                    <Chip
                      label={`${user.first_name} ${user.last_name?.charAt(0)}.`}
                      variant="badge"
                      size="md"
                      icon={
                        user.profile_image ? (
                          <Avatar src={user.profile_image} sx={avatarSx} />
                        ) : undefined
                      }
                      sx={chipSx}
                    />
                  </Box>
                )}

                {showDelete && (
                  <Box
                    id="profile-video-delete-button"
                    sx={trashIconContainerSx}
                    onClick={() => handleDeleteClick(document)}
                  >
                    <AppIcon
                      iconName="Trash01"
                      diameter="16px"
                      color="Colors/Foreground/fg-quaternary"
                    />
                  </Box>
                )}
              </Box>

              <Box sx={textContentContainerSx}>
                <Typography
                  variant="text-lg-semibold"
                  color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
                >
                  {document.title}
                </Typography>
                <Typography
                  variant="text-sm-regular"
                  color={({ backrs }) => backrs.colors['Colors/Text/text-secondary']}
                >
                  {document.description}
                </Typography>
              </Box>

              <Box sx={storyTypeChipContainerSx}>
                <Chip
                  label={
                    document.question_id?.charAt(0).toUpperCase() + document.question_id?.slice(1)
                  }
                  variant="pill"
                  size="lg"
                  icon={<AppIcon iconName="VideoRecorder" diameter="16px" color="Brand/500" />}
                  sx={storyTypeChipSx}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
};

const documentsContainerSx =
  (fixedWidth: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: backrs.spacing['spacing-2xl'],
    rowGap: { xs: backrs.spacing['spacing-xl'] },
    mt: backrs.spacing['spacing-xs'],
    ...(fixedWidth && {
      width: { xs: '100%', md: '70vw', lg: '50vw', xl: '45vw' },
    }),
  });

const videoCardContainerSx: SxProps<Theme> = ({ backrs }) => ({
  maxWidth: '20rem',
  borderRadius: backrs.radius['radius-xl'],
  overflow: 'hidden',
  border: `2px solid ${backrs.colors['Gray/900']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
});

const chipContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'fit-content',
};

const avatarSx: SxProps<Theme> = ({ backrs }) => ({
  width: 20,
  height: 20,
  marginLeft: backrs.spacing['spacing-xs'],
});

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  objectFit: 'contain',
  gap: backrs.spacing['spacing-lg'],
  pl: backrs.spacing['spacing-xs'],
  pr: backrs.spacing['spacing-lg'],
  height: 32,
});

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  '&:last-child': {
    gap: backrs.spacing['spacing-2xl'],
  },
  paddingY: backrs.spacing['spacing-2xl'],
  paddingX: backrs.spacing['spacing-xl'],
});

const textContentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-none'],
});

const storyTypeChipContainerSx: SxProps<Theme> = {
  width: 'fit-content',
};

const storyTypeChipSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Brand/200']}`,
  backgroundColor: backrs.colors['Brand/50'],
  color: backrs.colors['Brand/700'],
  objectFit: 'contain',
  gap: backrs.spacing['spacing-sm'],
  height: ICON_SIZES['32px'],
});

const userAndTrashContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const trashIconContainerSx: SxProps<Theme> = {
  justifySelf: 'flex-end',
  cursor: 'pointer',
};
